/* src/pages/PlanningAppLanding.jsx
   route:  /planningapp
   ——————————————————————————————————————————
   Stripe‑Connect‑style landing page for the resident‑facing Planning App.
   Re‑uses the global LandingPage.css tokens + slices.
*/

import React, { useState } from "react";
import { motion }           from "framer-motion";
import Logo                 from "../../assets/images/wui-go_icon_transparent.png";
import "./LandingPage.css";               // ← reuse the existing styles
import {
  getFirestore, collection, addDoc, serverTimestamp
} from "firebase/firestore";

export default function PlanningAppLanding() {
  const db = getFirestore();

  /* simple email capture */
  const [email, setEmail]         = useState("");
  const [status, setStatus]       = useState("");

  const signUp = async (e) => {
    e.preventDefault();
    setStatus("Submitting…");
    try {
      await addDoc(collection(db, "planningapp_signups"), {
        email: email.trim(), createdAt: serverTimestamp()
      });
      setStatus("Thanks! We’ll keep you posted.");
      setEmail("");
    } catch (err) {
      console.error(err);
      setStatus("Something went wrong — please try again.");
    }
  };

  /* reusable feature block */
  const Feature = ({ caption, title, children, flip }) => (
    <section
      className={`Copy HomepageFrontdoor__copyHero${flip ? " flip" : ""}`}
      style={{ "--paddingRight": "var(--columnPaddingLarge)" }}
    >
      <header className="Copy__header">
        <h2 className="Copy__caption">{caption}</h2>
        <h1 className="Copy__title">{title}</h1>
      </header>
      <div className="Copy__body">{children}</div>
    </section>
  );

  return (
    <div className="antialiased text-gray-900">

      {/* ——— Sticky header ——— */}
      <header className="SiteHeader">
        <div className="SiteHeader__inner">
          <div className="SiteHeader__logo"><img src={Logo} alt="WUI‑Go" /></div>
          <nav className="SiteHeader__nav">
            {/* anchors for smooth‑scroll if needed */}
            {/*<a href="#learn"  className="SiteHeader__link">Features</a>
            <a href="#signup" className="SiteHeader__link">Early access</a>*/}
          </nav>
        </div>
      </header>

      {/* ——— Hero ——— */}
      <section className="ConnectHero">
        <div className="ConnectHero__inner">
            {/* LEFT — headline + copy */}
            <div className="ConnectHero__copy">
            <h1 className="ConnectHero__title">
                Personalized wildfire evacuation&nbsp;planning
            </h1>
            <p className="ConnectHero__subtitle">
            WUI-Go personalizes evacuation information to each individual’s unique circumstances —  pets, children, relatives, or other needs. <br/> <br/>
            Information is accessible through diverse customized formats, such as multiple languages, interactive maps and AI-generated audio podcasts
            </p>

            <form id="signup" className="Hero__form" onSubmit={signUp}>
                <input
                required
                type="email"
                value={email}
                placeholder="Email address"
                onChange={e=>setEmail(e.target.value)}
                className="Hero__input"
                />
                <button className="Hero__button">Get early access</button>
            </form>
            {status && <p className="mt-2 text-sm text-gray-500">{status}</p>}
            </div>

            {/* RIGHT — illustrative mock‑up (img, svg, etc.) */}
            <div className="ConnectHero__art">
            {/* put any PNG / SVG of a phone‑mockup here; a blank div works as placeholder */}
            <div className="PhoneMock" />
            </div>
        </div>
     </section>
      
      {/* ——— Core value prop ——— */}
      <Feature
        caption="Embed evacuation info with confidence"
        title="Our resident‑facing app personalizes guidance to each individual"
      >
        <p>
          Whether they have pets, small children, need to pick up a relative, or
          lack access to a car — WUI‑Go tailors routes, checklists and
          notifications in the format that suits them best, from interactive
          maps to AI‑generated audio podcasts in multiple languages.
        </p>
      </Feature>

      {/* ——— Learning feature ——— */}
      <Feature
        id="learn"
        caption="Learn best practices"
        title="Visualize route options &amp; trade‑offs"
      >
        <p>
          Simulate wildfire evacuation for your neighborhood and compare routes
          by time, congestion and safety. Understand where bottlenecks occur
          before the next emergency strikes.
        </p>
      </Feature>

      {/* ——— Checklist feature ——— */}
      <Feature
        flip
        caption="Personalized preparedness"
        title="Build your wildfire checklist in seconds"
      >
        <p>
          WUI‑Go synthesizes FEMA, state and county guidance into a single
          checklist personalised to your household — pets, children, elderly and
          more — delivered in your preferred language.
        </p>
      </Feature>

      {/* ——— Podcast feature ——— */}
      <Feature
        caption="Hands‑free content"
        title="Consume information as audio podcasts"
      >
        <p>
          Turn evacuation instructions into short podcasts so residents can
          learn on the go and share with family. Automatic translation included.
        </p>
      </Feature>

      {/* ——— Analytics feature ——— */}
      <Feature
        flip
        caption="Measure impact"
        title="Resident engagement analytics"
      >
        <p>
          Track how many households open, read or listen to each resource.
          Identify gaps and focus your outreach where it matters most.
        </p>
      </Feature>

      {/* ——— Simple footer ——— */}
      <footer className="Footer">
        <div className="Footer__inner">
          © {new Date().getFullYear()} WUI‑Go &nbsp;·&nbsp; All rights reserved.
        </div>
      </footer>
    </div>
  );
}